// ПРОВОДКА СУДЬИ ЦИТАТ В ВИЗАРД — строка 73 трекера Track C `3f819875cbb8819e82c7c74954dac4ee`, решение `s987.d3`
// (уточняет `s490.d4`), замер `3f819875cbb8819bbba4eae1aaf1c6f4`.
//
// ⭐ КОГДА ЗОВЁТСЯ. Тексты T1–T3 заканчиваются на шаге 3, поэтому судья зовётся в фоне на ВЫХОДЕ с шага 3
// (оболочка: шаг ≥ 4 и результата под текущий ввод нет), а не на кнопке шага 6. Шаги 4–5 дают ему время.
// Шаг 6 читает цитату из конверта; не готово или цитаты нет — карточки нет, индикатора нет (`s960.d1`,
// `s453.d2`). Сабмит (CTA шага 6) дожидается того же вызова в пределах транспорта (`httpCallLLM`, 3 × 30 с).
//
// ⭐ РЕЗУЛЬТАТ ЖИВЁТ В КОНВЕРТЕ ПОЛЕМ `quotes` = `{ key, system2 }` (схема 4, `session.js`). `key` — отпечаток
// ВХОДА судьи: тексты T1–T3 по этапам + несущие этапы (флаг `isCarrier` меняет промпт). Правка текста на
// шаге 3 или ползунка, двигающего несущую, даёт другой ключ — и старый результат перестаёт читаться
// (`quotesOf` → `null`) сам, без отдельного сброса в каждом поле деки (п. 4 `s987.d3`). Следующий выход с
// шага 3 зовёт судью заново. В ключе — хэш, а не тексты: второй копии сырья в конверте не нужно.
//
// ⭐ СБОЙ ТОЖЕ ПИШЕТСЯ. `selectQuotes` сам ловит сбой этапа (`not_evaluated`, `transport.ok = false`) и
// отдаёт то, что уцелело; рендер снимает §VI, когда реальных цитат меньше двух (`s882.d1`). Записанный сбой
// значит: шаг 6, Open, PDF и `/r/` видят ОДНО и то же. Второй шанс — один раз, на сабмите
// (`awaitQuotes({ retryFailed: true })`): там ожидание оправдано, на шагах 4–5 повтор молча жёг бы попытки.
//
// ⛔ СЫРЫЕ T1–T3 В ДОКУМЕНТ И СЛЕПОК НЕ ИДУТ (`s490.d1`): паспорт документа строится без `text`
// (`documentPassport.js`), `system2` несёт только вырезки, прошедшие verbatim (`src/quotes/verbatim.js`).

import { selectQuotes } from '../quotes/index.js';
import { httpCallLLM } from '../quotes/transport.js';
import { textAt } from './submitChain.js';

/** Тексты судьи по этапам — выровнены по числу ползунков, как `inputOfSession`. */
export function judgeStagesOf(session) {
  const { sliders, text } = session.input;
  const n = Array.isArray(sliders) ? sliders.length : 0;
  return Array.from({ length: n }, (_, i) => textAt(text, i));
}

/**
 * cyrb53 — 53-битный некриптографический хэш строки. Здесь нужен только детектор «вход изменился», а не
 * секрет: вход и так лежит в том же конверте рядом. Синхронный — `crypto.subtle` асинхронен, а ключ
 * читается в рендере.
 */
function cyrb53(str) {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

/**
 * Ключ входа судьи.
 * @param {Array<{T1:string,T2:string,T3:string}>} stages
 * @param {number[]} carrier несущие этапы (`carrierStages.indices` паспорта)
 */
export function quotesKey(stages, carrier) {
  return `q1:${stages.length}:${cyrb53(JSON.stringify({ t: stages.map((s) => [s.T1, s.T2, s.T3]), c: [...carrier].sort((a, b) => a - b) }))}`;
}

/** Есть ли вообще что судить — хоть одно непустое поле. Без текста судья не зовётся и сети нет. */
export const hasText = (stages) => stages.some((s) => [s.T1, s.T2, s.T3].some((v) => v.trim() !== ''));

/**
 * `system2` из конверта, если он посчитан под ТЕКУЩИЙ вход, иначе `null`.
 * @param {object} session конверт
 * @param {number[]} carrier несущие этапы текущего паспорта
 */
export function quotesOf(session, carrier) {
  const q = session.quotes;
  if (!q || typeof q !== 'object' || !q.system2) return null;
  return q.key === quotesKey(judgeStagesOf(session), carrier) ? q.system2 : null;
}

/** Записанный результат — сбой транспорта (хоть один этап без ответа)? */
export const failed = (q) => Boolean(q && q.system2 && q.system2.transport && q.system2.transport.ok === false);

/**
 * Прогон судьи по конверту. Без текста — `system2 = null` (нечего цитировать, §VI снимется сам).
 * @returns {Promise<{key: string, system2: object|null}>}
 */
export async function runQuotes(session, carrier, { judge = httpCallLLM, log } = {}) {
  const stages = judgeStagesOf(session);
  const key = quotesKey(stages, carrier);
  if (stages.length === 0 || !hasText(stages)) return { key, system2: null };
  const system2 = await selectQuotes(stages, { carrierStages: carrier, judge, ...(log ? { log } : {}) });
  return { key, system2 };
}

/**
 * Бегунок с памятью вызовов: один вызов на ключ, сколько бы раз его ни попросили (эффект оболочки, сабмит,
 * StrictMode монтирует дважды). Ключ сменился — новый вызов; старый доживает, но его результат уже не
 * читается (`quotesOf`).
 *
 * @param {{judge?: Function, log?: Function}} [deps]
 */
export function createQuotesRunner(deps = {}) {
  const inflight = new Map();
  return {
    /**
     * @param {object} session
     * @param {number[]} carrier
     * @param {{retry?: boolean}} [opts] `retry` — забыть прежний вызов этого ключа и позвать заново
     */
    run(session, carrier, { retry = false } = {}) {
      const key = quotesKey(judgeStagesOf(session), carrier);
      if (retry) inflight.delete(key);
      if (!inflight.has(key)) inflight.set(key, runQuotes(session, carrier, deps));
      return inflight.get(key);
    },
  };
}
